/**
 * Private provider notes (2026-10-21): only providers and the office see them, never the facility.
 *
 * Mirrors backend/src/routes/emergeDaySheetPrivateNotes.js. This module and PrivateNotes.tsx are the only frontend
 * code that names the endpoint (backend privateNotesNeverLeave.test.js holds that), so nothing that prints, exports
 * or sends a manifest can pick a private note up.
 */
import { api } from "../../../services/api";

export type PrivateNoteAccess = "CARE" | "OFFICE" | "ADMIN" | "AUTHOR_ONLY";

export interface PrivateNote {
  id: string;
  /** Plain text, exactly as written. Always rendered as text, never as markup. */
  body: string;
  created_at: string;
  care_basis: "CARE_ASSIGNMENT" | "FACILITY_COVERAGE";
  author: { name: string | null; is_you: boolean };
  facility: { id: string; name: string | null };
  /** Written on a resident record later merged into this one. */
  from_merged_record: boolean;
}

export interface PrivateNotesPage {
  notes: PrivateNote[];
  truncated: boolean;
  limit: number;
  access: PrivateNoteAccess;
  can_write: boolean;
  max_length: number;
}

export interface PrivateNotesResult<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  message?: string;
  /** Absent with no code: the server was never reached (services/api contract). */
  status?: number;
}

const path = (facilityId: string, patientId: string) =>
  `/emerge/day-sheet/${encodeURIComponent(facilityId)}/patients/${encodeURIComponent(patientId)}/private-notes`;

export const privateNotesApi = {
  list: async (facilityId: string, patientId: string): Promise<PrivateNotesResult<PrivateNotesPage>> =>
    (await api.get(path(facilityId, patientId))) as PrivateNotesResult<PrivateNotesPage>,

  /** One key per attempt: a retry with the same key is the same note (200 replayed), never a second one. */
  add: async (
    facilityId: string,
    patientId: string,
    body: string,
    idempotencyKey: string,
  ): Promise<PrivateNotesResult<{ note: PrivateNote | null; replayed: boolean }>> =>
    (await api.post(path(facilityId, patientId), { body }, {
      headers: { "Idempotency-Key": idempotencyKey },
    })) as PrivateNotesResult<{ note: PrivateNote | null; replayed: boolean }>,
};
